"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

const stats = [
  { value: 38, suffix: "%", label: "Avg. Cost Reduction", icon: "ArrowTrendingDownIcon" },
  { value: 97, suffix: "+", label: "Client Engagements", icon: "UserGroupIcon" },
  { value: 15, suffix: "+", label: "Countries Served", icon: "GlobeAltIcon" },
  { value: 12, suffix: "yr", label: "Industry Experience", icon: "ClockIcon" },
];

const differentiators = [
  { icon: "AdjustmentsHorizontalIcon", text: "Tailored operational solutions" },
  { icon: "ChartBarIcon", text: "Data-driven decision making" },
  { icon: "GlobeAltIcon", text: "Global logistics expertise" },
  { icon: "CurrencyDollarIcon", text: "Cost optimization strategies" },
  { icon: "ArrowPathIcon", text: "Scalable systems for long-term growth" },
];

function Counter({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1600;
          const start = performance.now();
          const update = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setCount(Math.round(target * ease));
            if (p < 1) requestAnimationFrame(update);
          };
          requestAnimationFrame(update);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="counter-num">
      {count}
      {suffix}
    </span>
  );
}

export default function WhyChooseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll(".reveal").forEach((i) => observer.observe(i));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-28 bg-[#0C1B33]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left: Heading + differentiators */}
          <div className="lg:col-span-5 space-y-10">
            <div className="reveal">
              <p className="text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
                Why StanDave
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-700 text-cream leading-tight tracking-tight">
                Built for Results.{" "}
                <span className="text-gold">Not Reports.</span>
              </h2>
            </div>
            <p className="reveal delay-100 text-[18px] font-body font-500 text-cream/80 leading-[1.75]">
              We prioritize measurable outcomes over generic advice. Every engagement is built around your specific operational challenges and growth objectives.
            </p>

            <ul className="space-y-4">
              {differentiators.map((item, i) => (
                <li
                  key={item.text}
                  className={`reveal delay-${(i + 2) * 100} flex items-center gap-4 group`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[rgba(184,134,11,0.15)] border border-[rgba(184,134,11,0.3)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(184,134,11,0.25)] transition-colors">
                    <Icon
                      name={item.icon as "GlobeAltIcon"}
                      size={16}
                      className="text-gold"
                    />
                  </div>
                  <span className="text-[17px] font-body font-500 text-cream/90">
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Stats grid */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className={`reveal delay-${(i + 1) * 100} service-card rounded-2xl p-8 text-center group`}
              >
                <div className="w-10 h-10 rounded-lg bg-[rgba(184,134,11,0.15)] flex items-center justify-center mx-auto mb-4">
                  <Icon
                    name={stat.icon as "GlobeAltIcon"}
                    size={18}
                    className="text-gold"
                  />
                </div>
                <div className="text-4xl md:text-5xl font-display font-700 text-gold mb-2">
                  <Counter target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-[15px] font-display font-600 uppercase tracking-widest text-cream/70">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}